import React, { useCallback, useState, useMemo } from 'react'
import { useDropzone } from 'react-dropzone'
import { Upload, FileImage, FileVideo, Music, FileText, AlertCircle } from 'lucide-react'
import clsx from 'clsx'

type MediaFilter = 'video' | 'audio' | 'images' | 'documents' | null

const ACCEPT_MAP: Record<string, Record<string, string[]>> = {
  video: { 'video/*': ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.ts', '.mts', '.m2ts'] },
  audio: { 'audio/*': ['.mp3', '.wav', '.ogg', '.flac', '.aac', '.m4a'] },
  images: { 'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff'] },
  documents: {
    'application/pdf': ['.pdf'],
    'text/plain': ['.txt'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  },
}

const ALL_ACCEPT = Object.values(ACCEPT_MAP).reduce(
  (acc, group) => ({ ...acc, ...group }), {}
)

const FILTER_LABELS: Record<string, { label: string; formats: string }> = {
  video: { label: 'Video', formats: 'MP4, MOV, AVI, WebM, FLV' },
  audio: { label: 'Audio', formats: 'MP3, WAV, OGG, FLAC, AAC' },
  images: { label: 'Images', formats: 'JPG, PNG, GIF, BMP, TIFF' },
  documents: { label: 'Documents', formats: 'PDF, TXT, DOC, DOCX' },
}

interface FileUploadProps {
  onFilesUpload: (files: File[]) => void
  isUploading: boolean
  maxFiles?: number
  acceptedTypes?: string[]
  /** When true, accept all file types (disables dropzone filtering). */
  acceptAll?: boolean
  className?: string
}

const FileUpload: React.FC<FileUploadProps> = ({
  onFilesUpload,
  isUploading,
  maxFiles = 500,
  acceptAll = false,
  className
}) => {
  const [mediaFilter, setMediaFilter] = useState<MediaFilter>(null)

  const accept = useMemo(() => {
    if (acceptAll && !mediaFilter) return undefined  // accept everything
    if (!mediaFilter) return ALL_ACCEPT
    return ACCEPT_MAP[mediaFilter]
  }, [mediaFilter, acceptAll])

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      onFilesUpload(acceptedFiles)
    }
  }, [onFilesUpload])

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    accept,
    maxFiles,
    disabled: isUploading
  })

  const toggleFilter = (filter: MediaFilter) => {
    setMediaFilter(prev => prev === filter ? null : filter)
  }

  const filters: Array<{ key: MediaFilter; icon: typeof FileVideo; activeColor: string; bgFrom: string; bgTo: string; ringColor: string }> = [
    { key: 'video', icon: FileVideo, activeColor: 'text-[#0f1218]', bgFrom: 'from-[var(--rq-blue)]', bgTo: 'to-[var(--brand-light)]', ringColor: 'ring-blue-300/30' },
    { key: 'audio', icon: Music, activeColor: 'text-[#0f1218]', bgFrom: 'from-[var(--rq-amber)]', bgTo: 'to-[#ffd08a]', ringColor: 'ring-amber-300/30' },
    { key: 'images', icon: FileImage, activeColor: 'text-[#0f1218]', bgFrom: 'from-[var(--rq-magenta)]', bgTo: 'to-[#ff72c4]', ringColor: 'ring-pink-300/30' },
    { key: 'documents', icon: FileText, activeColor: 'text-[#0f1218]', bgFrom: 'from-[#96a3b8]', bgTo: 'to-[#c0cad8]', ringColor: 'ring-white/35' },
  ]

  const inactiveStyles: Record<string, { bgFrom: string; bgTo: string; textColor: string }> = {
    video: { bgFrom: 'from-[#1e2d52]/70', bgTo: 'to-[#141f3a]/70', textColor: 'text-blue-200/90' },
    audio: { bgFrom: 'from-[#4f3718]/65', bgTo: 'to-[#36250f]/70', textColor: 'text-amber-200/90' },
    images: { bgFrom: 'from-[#4f183d]/65', bgTo: 'to-[#37112a]/70', textColor: 'text-pink-200/90' },
    documents: { bgFrom: 'from-[#2f3847]/70', bgTo: 'to-[#242c38]/70', textColor: 'text-white/85' },
  }

  return (
    <div className={clsx('w-full', className)}>
      {/* Media type filter buttons — outside the dropzone */}
      <div className="flex items-center justify-center gap-3 md:gap-4 mb-4">
        {filters.map(({ key, icon: Icon }) => {
          const isActive = mediaFilter === key
          const inactive = inactiveStyles[key!]
          const active = filters.find(f => f.key === key)!
          return (
            <button
              key={key}
              type="button"
              onClick={() => toggleFilter(key)}
              className={clsx(
                'flex flex-col items-center gap-1.5 p-2 md:p-3 rounded-xl md:rounded-2xl transition-colors duration-150 cursor-pointer',
                isActive
                  ? `bg-gradient-to-br ${active.bgFrom} ${active.bgTo} shadow-md ring-2 ${active.ringColor}`
                  : `bg-gradient-to-br ${inactive.bgFrom} ${inactive.bgTo} shadow-sm hover:shadow-md`
              )}
              title={`Filter: ${FILTER_LABELS[key!].label} only`}
            >
              <Icon className={clsx(
                'w-7 h-7 md:w-9 md:h-9',
                isActive ? active.activeColor : inactive.textColor
              )} />
              <span className={clsx(
                'text-[10px] md:text-xs font-semibold',
                isActive ? 'text-white' : 'text-[var(--rq-text-soft)]'
              )}>
                {FILTER_LABELS[key!].label}
              </span>
            </button>
          )
        })}
      </div>

      {/* Active filter indicator */}
      {mediaFilter && (
        <div className="text-center mb-3">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/10 border border-white/15 rounded-full text-xs text-[var(--rq-text-soft)] font-medium">
            Accepting: {FILTER_LABELS[mediaFilter].formats}
            <button
              type="button"
              onClick={() => setMediaFilter(null)}
              className="ml-1 text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] font-bold"
              title="Clear filter"
            >
              &times;
            </button>
          </span>
        </div>
      )}

      <div
        {...getRootProps()}
        className={clsx(
          'relative border-2 border-dashed rounded-2xl p-6 md:p-10 text-center cursor-pointer transition-all duration-200 rq-card',
          isDragActive
            ? 'border-[var(--btn)] bg-[var(--btn-softer)] shadow-lg scale-[1.02]'
            : 'bg-black/15 border-white/15 hover:border-white/35 hover:bg-black/10',
          isUploading && 'opacity-60 cursor-not-allowed'
        )}
      >
        <input {...getInputProps()} />

        <div className="flex flex-col items-center space-y-4 md:space-y-5">
          {isDragActive ? (
            <>
              <div className="p-3 md:p-4 bg-[var(--btn-softer)] rounded-full border border-white/10">
                <Upload className="w-12 h-12 md:w-16 md:h-16 text-[var(--brand-light)]" />
              </div>
              <div className="space-y-2">
                <div className="text-xl md:text-2xl font-semibold text-[var(--rq-text)]">
                  Drop files here...
                </div>
                <div className="text-sm text-[var(--rq-text-soft)]">
                  Release to upload
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-3 md:space-y-4">
                <div className="space-y-1 md:space-y-2">
                  <h3 className="text-base md:text-lg font-semibold text-[var(--rq-text)] tracking-tight">
                    {isUploading ? 'Uploading your files...' : 'Drop your files here'}
                  </h3>
                  <p className="text-sm text-[var(--rq-text-soft)] font-normal">
                    or tap to browse
                  </p>
                </div>

                <button
                  type="button"
                  className="rq-btn-primary inline-flex items-center gap-2 md:gap-2.5 px-6 md:px-8 py-3 md:py-3.5 text-base min-h-[48px] disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isUploading}
                >
                  <Upload className="w-5 h-5" />
                  Choose Files
                </button>

                <p className="text-xs md:text-sm text-[var(--rq-text-soft)] font-medium pt-1">
                  {mediaFilter
                    ? FILTER_LABELS[mediaFilter].formats
                  : 'Video, audio, images, or documents'}
                  <span className="hidden sm:inline text-[var(--rq-text-soft)] font-normal"> (up to {maxFiles} files)</span>
                </p>
              </div>
            </>
          )}
        </div>

        {!isDragActive && (
          <div className="mt-5 md:mt-6 pt-4 md:pt-5 border-t border-white/10">
            <p className="text-xs text-[var(--rq-text-soft)] font-medium">
              {mediaFilter ? (
                <span>{FILTER_LABELS[mediaFilter].formats} files only</span>
              ) : (
                <>
                  <span className="hidden sm:inline">MP4, MOV, MP3, WAV, JPG, PNG, PDF, TXT, and more</span>
                  <span className="sm:hidden">Video, audio, images, docs</span>
                </>
              )}
              <span> &bull; Up to 2GB per file</span>
            </p>
          </div>
        )}
      </div>

      {fileRejections.length > 0 && (
        <div className="mt-4 p-4 bg-red-900/20 border border-red-700/40 rounded-xl shadow-sm">
          <div className="flex items-center gap-2 text-red-300 mb-2">
            <div className="p-1 bg-red-900/35 rounded-full">
              <AlertCircle className="w-4 h-4" />
            </div>
            <span className="text-sm font-semibold">Some files were rejected</span>
          </div>
          <ul className="ml-7 space-y-1 text-sm text-red-300">
            {fileRejections.map(({ file, errors }) => (
              <li key={file.name} className="font-medium">
                {file.name}
                <span className="text-red-200 font-normal"> – {errors.map(e => e.message).join(', ')}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default FileUpload
